/**
 * RequireAuth.tsx — Route guard for the ɳSentry SaaS app zone
 *
 * Purpose:    Wraps authed routes under /app; redirects to /app/login when
 *             unauthenticated, preserving the origin location for post-login return.
 * Inputs:     useAuth() (user, loading)
 * Outputs:    children when authenticated; spinner while loading; redirect otherwise
 * Constraints: Must be rendered inside AuthProvider + BrowserRouter
 * SOT:        nsentry SaaS auth zone
 */
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-bg">
        <div className="animate-pulse space-y-4 p-6" aria-label="Loading…" role="status">
          <div className="h-10 w-48 rounded-md bg-brand-surface-2" />
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/app/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
